import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Image, Button, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'react-bootstrap-icons';
import exhibitionService from '../../services/exhibitionService';
import config from '../../config';

const ExhibitionDetailPage = () => {
  const { exhibitionId } = useParams();
  const navigate = useNavigate();
  const [exhibition, setExhibition] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  console.log('ExhibitionDetailPage loaded with ID:', exhibitionId);
  console.log('useParams result:', useParams());

  const loadExhibitionDetails = useCallback(async () => {
    console.log('loadExhibitionDetails called with ID:', exhibitionId);
    try {
      setLoading(true);
      
      console.log('Loading exhibition details for ID:', exhibitionId);
      
      // Load exhibition details
      const exhibitionData = await exhibitionService.getExhibitionById(exhibitionId);
      console.log('Exhibition data:', exhibitionData);
      setExhibition(exhibitionData);
      
      // Load exhibition photos
      console.log('Loading photos for exhibition ID:', exhibitionId);
      const photosData = await exhibitionService.getExhibitionPhotos(exhibitionId);
      console.log('Photos data:', photosData);
      setPhotos(photosData);
      
      setError(null);
    } catch (error) {
      console.error('Failed to load exhibition details:', error);
      setError('Failed to load exhibition details');
    } finally {
      setLoading(false);
    }
  }, [exhibitionId]);

  useEffect(() => {
    console.log('useEffect triggered with exhibitionId:', exhibitionId);
    if (exhibitionId) {
      console.log('Calling loadExhibitionDetails...');
      loadExhibitionDetails();
    } else {
      console.log('exhibitionId is falsy:', exhibitionId);
    }
  }, [exhibitionId, loadExhibitionDetails]);

  const handleBack = () => {
    navigate(-1);
  };

  const handlePhotoClick = (index) => {
    console.log('Photo clicked with index:', index);
    setPhotoIndex(index);
    setShowLightbox(true);
  };

  const handleCloseLightbox = () => {
    setShowLightbox(false);
  };

  const handlePreviousPhoto = () => {
    setPhotoIndex((photoIndex + photos.length - 1) % photos.length);
  };

  const handleNextPhoto = () => {
    setPhotoIndex((photoIndex + 1) % photos.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      
      switch (e.key) {
        case 'Escape':
          handleCloseLightbox();
          break;
        case 'ArrowLeft':
          handlePreviousPhoto();
          break;
        case 'ArrowRight':
          handleNextPhoto();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, photoIndex, photos.length]);

  // Prepare images for lightbox
  const lightboxImages = photos.map(photo => `${config.API_URL}${photo.image_url}`);
  console.log('Lightbox images:', lightboxImages);

  if (loading) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center">
          <h3>Error</h3>
          <p>{error}</p>
          <Button variant="primary" onClick={handleBack}>
            Go Back
          </Button>
        </div>
      </Container>
    );
  }

  if (!exhibition) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center">
          <h3>Exhibition not found</h3>
          <Button variant="primary" onClick={handleBack}>
            Go Back
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="exhibition-detail-page">
      <style>
        {`
          .lightbox-modal .modal-content {
            background-color: #000 !important;
            border: none;
            border-radius: 20px !important;
          }
          .lightbox-modal .modal-backdrop {
            background-color: rgba(0, 0, 0, 0.9) !important;
          }
          .lightbox-modal .btn-light {
            background-color: rgba(255, 255, 255, 0.9) !important;
            border: none !important;
            color: #000 !important;
          }
          .lightbox-modal .btn-light:hover {
            background-color: rgba(255, 255, 255, 1) !important;
          }
          .lightbox-modal .btn-light:disabled {
            background-color: rgba(255, 255, 255, 0.3) !important;
            color: #666 !important;
          }
        `}
      </style>
      
      <Container className="py-5 mt-5">
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <Button variant="outline-secondary" onClick={handleBack} className="me-3">
            <ArrowLeft /> Back
          </Button>
          <h2 className="mb-0">{exhibition.title}</h2>
        </div>

        {/* Exhibition Info */}
        <Row className="mb-5">
          <Col md={6}>
            <Image 
              src={`${config.API_URL.replace('/api', '')}${exhibition.image_url}`}
              
              style={{ 
                width: '100%', 
                height: '400px', 
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
          </Col>
          <Col md={6}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title className="h4">{exhibition.title}</Card.Title>
                <Card.Text className="text-muted">
                  {exhibition.description}
                </Card.Text>
                <div className="mt-3">
                  <small className="text-muted">
                    Created: {new Date(exhibition.created_at).toLocaleDateString()}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Photos Section */}
        <div className="mb-4">
          <h3>Photos ({photos.length})</h3>
          {photos.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No photos available for this exhibition.</p>
            </div>
          ) : (
            <Row xs={1} md={2} lg={3} className="g-4">
              {photos.map((photo, index) => (
                <Col key={photo.id}>
                  <Card 
                    className="h-100 photo-card" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handlePhotoClick(index)}
                  >
                    <Card.Img 
                      variant="top" 
                      src={`${config.API_URL}${photo.image_url}`}
                      
                      style={{ 
                        height: '250px', 
                        objectFit: 'cover'
                      }}
                    />
                    <Card.Body>
                      <Card.Title className="h6">{photo.title}</Card.Title>
                      {photo.description && (
                        <Card.Text className="text-muted small">
                          {photo.description}
                        </Card.Text>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Container>

      {/* Custom Lightbox Modal */}
      <Modal 
        show={showLightbox} 
        onHide={handleCloseLightbox}
        size="lg"
        centered
        className="lightbox-modal"
        backdrop="static"
      >
        <Modal.Body className="p-0 position-relative bg-dark" style={{ borderRadius: '30px' }}>
          <div className="d-flex align-items-center justify-content-center position-relative">
            <Image 
              src={lightboxImages[photoIndex]}
              style={{ 
                width: '100%', 
                maxHeight: '80vh', 
                objectFit: 'contain',
                borderRadius: '20px'
              }}
            />
          </div>
          
          {(photos[photoIndex]?.title || photos[photoIndex]?.description) && (
            <div className="p-3 bg-dark text-white">
              {photos[photoIndex]?.title && (
                <h5 className="mb-2 text-white">{photos[photoIndex].title}</h5>
              )}
              {photos[photoIndex]?.description && (
                <p className="mb-0 text-light">{photos[photoIndex].description}</p>
              )}
              <small className="text-light">
                {photoIndex + 1} of {photos.length}
              </small>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Fixed Position Buttons - Outside Modal */}
      {showLightbox && (
        <>
          {/* Close Button - Top Right Corner */}
          <Button
            variant="light"
            className="position-fixed rounded-circle"
            style={{ 
              zIndex: 9999, 
              width: '50px', 
              height: '50px',
              top: '100px',
              right: '20px'
            }}
            onClick={handleCloseLightbox}
          >
            <X size={20} />
          </Button>

          {/* Previous Button - Left Edge */}
          <Button
            variant="light"
            className="position-fixed rounded-circle"
            style={{ 
              zIndex: 9999, 
              width: '60px', 
              height: '60px',
              top: '50%',
              left: '20px',
              transform: 'translateY(-50%)'
            }}
            onClick={handlePreviousPhoto}
            disabled={photos.length <= 1}
          >
            <ChevronLeft size={25} />
          </Button>

          {/* Next Button - Right Edge */}
          <Button
            variant="light"
            className="position-fixed rounded-circle"
            style={{ 
              zIndex: 9999, 
              width: '60px', 
              height: '60px',
              top: '50%',
              right: '20px',
              transform: 'translateY(-50%)'
            }}
            onClick={handleNextPhoto}
            disabled={photos.length <= 1}
          >
            <ChevronRight size={25} />
          </Button>
        </>
      )}
    </div>
  );
};

export default ExhibitionDetailPage; 